import type { MaybeRef } from 'vue'
import type { AnalyticsState, CurrentSearchState, TrackFunction } from './searchDiagnosticsSession'
import type { SearchDiagnosticResult } from '~/utils/pendo/search-diagnostics'
import type { SearchDidYouMeanSuggestion } from '~/utils/solr/spellcheck-suggestion'
import {
  getCurrentInstance,
  onBeforeUnmount,
  shallowReactive,
  toValue,
  watch,
  watchPostEffect,
} from 'vue'
import {
  buildSearchResultClickedPayload,
  SEARCH_DIAGNOSTICS_EVENTS,
} from '~/utils/pendo/search-diagnostics'
import { finalizeCurrentSearchSession, normalizeSortOrder } from './searchDiagnosticsSession'

import { recordDidYouMeanShown, trackDidYouMeanSelected } from './searchDiagnosticsDidYouMean'
import { processSearchState } from './searchDiagnosticsRendering'

interface SearchDiagnosticsAnalyticsOptions {
  query: MaybeRef<string>
  page: MaybeRef<number>
  routeSearchSignature: MaybeRef<string>
  isLoading: MaybeRef<boolean>
  results: MaybeRef<SearchDiagnosticResult[]>
  totalResults: MaybeRef<number>
  rows: MaybeRef<number>
  hasLicense: MaybeRef<boolean>
  didYouMeanSuggestion?: MaybeRef<SearchDidYouMeanSuggestion | null>
  transportRetried?: MaybeRef<boolean>
  hasError?: MaybeRef<boolean>
  errorStatus?: MaybeRef<number | null | undefined>
  filterCount?: MaybeRef<number>
  filterSignature?: MaybeRef<string>
  sortOrder?: MaybeRef<string>
  now?: () => number
  random?: () => number
}

export function useSearchDiagnosticsAnalytics(options: SearchDiagnosticsAnalyticsOptions) {
  if (!import.meta.client) {
    return {
      trackResultClicked: () => undefined,
      trackSearchSessionEnded: () => undefined,
      trackDidYouMeanSelected: (_query: string) => undefined,
    }
  }

  const { track } = usePendo()
  const { analytics } = useCookieConsent()

  const now = options.now ?? Date.now
  const random = options.random ?? Math.random
  const analyticsState = shallowReactive<AnalyticsState>({
    lastRenderedSearch: null,
    lastEmittedSignature: null,
    lastFailedSignature: null,
  })

  watchSearchAnalytics(options, analyticsState, track, analytics, now, random)
  registerSessionEnd(analyticsState, track, now)

  return {
    trackResultClicked: (result: SearchDiagnosticResult, rank: number) =>
      trackSearchResultClicked(result, rank, options, analyticsState, track),
    trackSearchSessionEnded: () => finalizeCurrentSearchSession(analyticsState, track, now),
    trackDidYouMeanSelected: (query: string) => {
      if (analytics.value) {
        trackDidYouMeanSelected(
          query,
          getCurrentSearchState(options),
          analyticsState,
          getDidYouMeanSuggestion(options),
          track,
        )
      }
    },
  }
}

function getCurrentSearchState(options: SearchDiagnosticsAnalyticsOptions): CurrentSearchState {
  return {
    query: toValue(options.query),
    page: toValue(options.page),
    routeSearchSignature: toValue(options.routeSearchSignature),
    isLoading: toValue(options.isLoading),
    results: toValue(options.results),
    totalResults: toValue(options.totalResults),
    rows: toValue(options.rows),
    hasError: options.hasError ? toValue(options.hasError) : false,
    errorStatus: options.errorStatus ? (toValue(options.errorStatus) ?? null) : null,
    transportRetried: options.transportRetried ? toValue(options.transportRetried) : false,
    filterCount: options.filterCount ? toValue(options.filterCount) : 0,
    filterSignature: options.filterSignature ? toValue(options.filterSignature) : '',
    sortOrder: normalizeSortOrder(options.sortOrder ? toValue(options.sortOrder) : ''),
  }
}

function trackSearchResultClicked(
  result: SearchDiagnosticResult,
  rank: number,
  options: SearchDiagnosticsAnalyticsOptions,
  analyticsState: AnalyticsState,
  track: TrackFunction,
) {
  const renderedSearch = analyticsState.lastRenderedSearch
  if (!renderedSearch || renderedSearch.sessionEnded) {
    return
  }

  const isFirstClickForSearch = !renderedSearch.clickedResult

  track(
    SEARCH_DIAGNOSTICS_EVENTS.resultClicked,
    buildSearchResultClickedPayload({
      searchId: renderedSearch.searchId,
      query: renderedSearch.query,
      result,
      rank,
      page: renderedSearch.page,
      totalResults: renderedSearch.totalResults,
      resultCountOnPage: renderedSearch.resultCountOnPage,
      hasLicense: toValue(options.hasLicense),
      isFirstClickForSearch,
    }),
  )

  renderedSearch.clickedResult = true
  renderedSearch.resultClickCount += 1
  renderedSearch.firstClickedRank ??= rank
}

function getDidYouMeanSuggestion(options: SearchDiagnosticsAnalyticsOptions) {
  return options.didYouMeanSuggestion ? toValue(options.didYouMeanSuggestion) : null
}

function registerSessionEnd(
  analyticsState: AnalyticsState,
  track: TrackFunction,
  now: () => number,
) {
  if (getCurrentInstance()) {
    const endSession = () => finalizeCurrentSearchSession(analyticsState, track, now)
    // Current search page is not keep-alive; normal unmount removes this pagehide listener.
    window.addEventListener('pagehide', endSession)
    onBeforeUnmount(() => {
      window.removeEventListener('pagehide', endSession)
      endSession()
    })
  }
}

function watchSearchAnalytics(
  options: SearchDiagnosticsAnalyticsOptions,
  analyticsState: AnalyticsState,
  track: TrackFunction,
  analytics: MaybeRef<boolean>,
  now: () => number,
  random: () => number,
) {
  watch(
    [() => getCurrentSearchState(options), () => toValue(analytics)],
    ([state, analyticsGranted]) => {
      if (!analyticsGranted) {
        return
      }
      processSearchState(state, analyticsState, track, now, random)
    },
    { immediate: true },
  )

  watchPostEffect(() => {
    if (toValue(analytics)) {
      recordDidYouMeanShown(
        getCurrentSearchState(options),
        analyticsState,
        getDidYouMeanSuggestion(options),
        track,
      )
    }
  })
}
